import React from "react";
import { Container, Divider, Skeleton, Text } from "@chakra-ui/react";
import { Language, Conference } from "../../types/conference.types";
import { Accomodation, Room } from "../../types/accomodation.types";
import { formatDateForInput } from "../../utils/date-formatter";
import AcceptAndSubmitFooter from "./AcceptAndSubmitFooter";
import AccomodationComponent from "./Accomodation";
import { AdditionalText } from "./AdditionalText";
import ApplicationsData from "./ApplicationsData";
import AttendanceFees from "./AttendanceFees";
import BillingAddress from "./BillingAddress";
import { CustomFieldRegistration } from "./CustomFieldRegistration";
import EscortFees from "./EscortFees";
import MailingAddress from "./MailingAddress";
import Meals from "./Meals";
import Presentations from "./Presentations";
import Programs from "./Programs";
import CongressHeader from "../common/Heading";
import Comment from "./Comment";
import { CustomMailingRegistration } from "./CustomMailingRegistration";
import { ModuleChangeStatus } from "../common/ModuleToggleIndicator";

interface ModuleChanges {
  mailing: ModuleChangeStatus;
  billing: ModuleChangeStatus;
  presentation: ModuleChangeStatus;
  participantFee: ModuleChangeStatus;
  escortFee: ModuleChangeStatus;
  accommodation: ModuleChangeStatus;
  meals: ModuleChangeStatus;
  activities: ModuleChangeStatus;
}

interface PreviewFormSectionsProps {
  congressEvent: Conference;
  congressId: number;
  formik: any;
  isEditMode: boolean;
  accomodations: Accomodation[];
  rooms: Room[];
  isLoadingAccomodations: boolean;
  onCopy: () => void;
  conditionsAccepted: boolean;
  onChangeConditions: (accepted: boolean) => void;
  isRegistering: boolean;
  language: Language;
  moduleChanges: ModuleChanges;
}

export const PreviewFormSections: React.FC<PreviewFormSectionsProps> = ({
  congressEvent,
  congressId,
  formik,
  isEditMode,
  accomodations,
  rooms,
  isLoadingAccomodations,
  onCopy,
  conditionsAccepted,
  onChangeConditions,
  isRegistering,
  language,
  moduleChanges,
}) => {
  const { configJson } = congressEvent;

  const showMailingSection = !!congressId && configJson.mailingDetailsEnabled;

  const showBillingSection =
    !!congressId &&
    configJson.paymentDetailsEnabled &&
    (formik.values.payment != null ||
      (isEditMode && moduleChanges.billing === "enabled"));

  const showPresentationSection =
    !!congressId && configJson.presentationEnabled;

  const showAttendanceFeeSection =
    !!congressId &&
    configJson.participantFeeEnabled &&
    (formik.values.participantFee != null ||
      (isEditMode && moduleChanges.participantFee === "enabled"));

  const showEscortFeeSection =
    !!congressId &&
    configJson.participantFeeEnabled &&
    (formik.values.escortFee != null ||
      (isEditMode && moduleChanges.escortFee === "enabled"));

  const accomodationEnabled =
    configJson.accommodationEnabled &&
    congressEvent.bookingStartDate &&
    congressEvent.bookingEndDate &&
    (formik.values.hotelBooking != null ||
      (isEditMode && moduleChanges.accommodation === "enabled"));

  const showMealsSection =
    !!congressId &&
    (configJson.lunchEnabled ||
      (isEditMode && moduleChanges.meals === "disabled"));

  const showProgramsSection =
    !!congressId &&
    (configJson.activityEnabled ||
      (isEditMode && moduleChanges.activities === "disabled"));

  const showOtherInfoSection = configJson.otherInfoEnabled && !!congressId;

  return (
    <>
      <Container centerContent>
        <CongressHeader>{congressEvent.name}</CongressHeader>
        <Text fontSize="xl" mt="4">
          {formatDateForInput(congressEvent.startDate)} -{" "}
          {formatDateForInput(congressEvent.endDate)}
        </Text>
      </Container>
      <ApplicationsData
        firstName={formik.values.participantRegister.firstName}
        lastName={formik.values.participantRegister.surName}
        title={formik.values.participantRegister.title}
        workplace={formik.values.participantRegister.workplace}
        email={formik.values.participantRegister.email}
        handleChange={formik.handleChange}
        setFieldValue={formik.setFieldValue}
        errors={formik.errors}
        touched={formik.touched}
      />
      {congressId && (
        <CustomFieldRegistration
          congressId={congressId}
          fieldValues={formik.values.participantRegister.valuesByFieldId}
          setFieldValue={formik.setFieldValue}
        />
      )}
      {showMailingSection && (
        <>
          <MailingAddress
            addressType={formik.values.address.addressType}
            city={formik.values.address.city}
            street={formik.values.address.street}
            postalCode={formik.values.address.postalCode}
            country={formik.values.address.country}
            phone={formik.values.address.phone}
            handleChange={formik.handleChange}
            setFieldValue={formik.setFieldValue}
            moduleChange={moduleChanges.mailing}
            isEditMode={isEditMode}
          />
          {congressId && (
            <CustomMailingRegistration
              congressId={congressId}
              fieldValues={formik.values.address.valuesByFieldId}
              setFieldValue={formik.setFieldValue}
            />
          )}
          <Divider my={{ base: 4 }} />
        </>
      )}
      {showBillingSection && (
        <>
          <BillingAddress
            payment={formik.values.payment?.payment}
            onCopy={onCopy}
            fullName={formik.values.payment?.fullName ?? ""}
            city={formik.values.payment?.city ?? ""}
            street={formik.values.payment?.street ?? ""}
            tax={formik.values.payment?.taxNumber ?? ""}
            phone={formik.values.payment?.phone ?? ""}
            email={formik.values.payment?.email ?? ""}
            country={formik.values.payment?.country ?? ""}
            postalCode={formik.values.payment?.postalCode ?? ""}
            handleChange={formik.handleChange}
            setFieldValue={formik.setFieldValue}
            fieldValues={formik.values.payment?.valuesByFieldId ?? new Map()}
            congressId={congressId}
            isCopyAvailable={configJson.mailingDetailsEnabled}
            moduleChange={moduleChanges.billing}
            isEditMode={isEditMode}
          />
          <Divider my={{ base: 4 }} />
        </>
      )}
      {showPresentationSection && (
        <>
          <Presentations
            congressId={congressId}
            handleChange={formik.handleChange}
            setFieldValue={formik.setFieldValue}
            presentations={formik.values.presentations || undefined}
            isEditMode={isEditMode}
            moduleChange={moduleChanges.presentation}
          />
          <Divider my={{ base: 4 }} />
        </>
      )}
      {showAttendanceFeeSection && (
        <>
          <AttendanceFees
            congressId={congressId}
            handleChange={formik.handleChange}
            setFieldValue={formik.setFieldValue}
            fee={
              formik.values.participantFee ?? {
                participationFeeId: undefined,
              }
            }
            isEditMode={isEditMode}
            moduleChange={moduleChanges.participantFee}
          />
          <Divider my={{ base: 4 }} />
        </>
      )}
      {showEscortFeeSection && (
        <EscortFees
          congressId={congressId}
          handleChange={formik.handleChange}
          setFieldValue={formik.setFieldValue}
          fee={
            formik.values.escortFee ?? {
              participationFeeId: undefined,
              numberOfAttendants: 0,
              attendantsName: "",
            }
          }
          isEditMode={isEditMode}
          moduleChange={moduleChanges.escortFee}
        />
      )}
      {accomodationEnabled && (
        <>
          {isLoadingAccomodations ? (
            <Skeleton height="200px" />
          ) : (
            <AccomodationComponent
              accomodations={accomodations}
              rooms={rooms}
              selectedAccomodation={formik.values.hotelBooking ?? null}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              setFieldValue={formik.setFieldValue}
              startDate={
                formatDateForInput(congressEvent.bookingStartDate) || ""
              }
              endDate={formatDateForInput(congressEvent.bookingEndDate) || ""}
              moduleChange={moduleChanges.accommodation}
              isEditMode={isEditMode}
            />
          )}
          <Divider my={{ base: 4 }} />
        </>
      )}
      {showMealsSection && (
        <>
          <Meals
            congressId={congressId}
            setFieldValue={formik.setFieldValue}
            meals={formik.values.meals || []}
            isEditMode={isEditMode}
            moduleChange={moduleChanges.meals}
          />
          <Divider my={{ base: 4 }} />
        </>
      )}
      {showProgramsSection && (
        <>
          <Programs
            congressId={congressId}
            setFieldValue={formik.setFieldValue}
            professionals={formik.values.professionals || []}
            socials={formik.values.socials || []}
            isEditMode={isEditMode}
            moduleChange={moduleChanges.activities}
          />
          <Divider my={{ base: 4 }} />
        </>
      )}
      {showOtherInfoSection && <AdditionalText congressId={congressId} />}
      <Comment
        handleChange={formik.handleChange}
        value={formik.values.comment}
      />
      <Divider my={{ base: 4 }} />
      {!isEditMode && (
        <AcceptAndSubmitFooter
          conditionsAccepted={conditionsAccepted}
          onChange={onChangeConditions}
          isLoading={isRegistering}
          language={language}
        />
      )}
    </>
  );
};
